import { createService } from 'v2/shared/services/create-service'
import { BackendServiceName } from 'v2/types'
import type {
  Channels,
  ChunkPayload,
  ConversationService,
  DeleteTaskActivityPayload,
  ExportViewPayload,
  HoldCallPayload,
  TaskActivityPayload,
  TaskPayload,
  TaskSearchPayload,
  TransferPayload,
  ViewCreatePayload,
  ViewRecordsParams,
} from 'v2/types/api/conversations'
import type { RecordingPayload } from 'v2/types/call'

const CONVERSATION_ENDPOINTS = {
  activeConversations: '/active',
  updateConversation: (uuid: string) => `/${uuid}`,
  addChunk: (conversationNumber: string) => `/${conversationNumber}/chunk`, // New endpoint
  getChunks: (conversationNumber: string, start?: number, end?: number) =>
    `/${conversationNumber}?${start ? `start=${start}&` : ''}${end ? `end=${end}&` : ''}get_templates=true&get_cached_chunks=true`,
  views: () => '/views',
  viewDetails: (uuid: string) => `/views/${uuid}`,
  getRecordingDetails: (conv_number: string) =>
    `/views/conversation/${conv_number}?get_recordings=true&get_open_conv=true`,
  updateView: (uuid: string) => `/views/${uuid}`,
  exportView: (uuid: string) => `/views/${uuid}/export`,
  downloadRecordings: '/views/download-recordings',
  exportRecording: (viewuuid: string, conversationId: string, recordingId: string) =>
    `/views/${viewuuid}/export/${conversationId}/recording/${recordingId}`,
  getTranscription: (recordingUUID: string) => `/transcriptions/${recordingUUID}`,
  favouriteView: (uuid: string) => `views/${uuid}/mark-favourite`,
  viewRecords: '/views/conversation',
  conversationDetail: (conversationNumber: string, queryParams?: Record<string, unknown>) => {
    const defaultParams = { get_templates: true, get_open_conv: true }
    const mergedParams = { ...defaultParams, ...queryParams }

    const queryString = new URLSearchParams(
      Object.entries(mergedParams)
        .filter(([, value]) => value !== undefined)
        .map(([key, value]) => [key, String(value)]),
    ).toString()

    return `/views/conversation/${conversationNumber}${queryString ? `?${queryString}` : ''}`
  },
  conversationFiltersConfiguration: '/views/configuration',
  recentContacts: (channel: Channels, senderId?: string) =>
    `/recent?offset=0&limit=15&channel=${channel}${senderId ? `&sender_id=${senderId}` : ''}`,
  liveAgents: (channel?: Channels) => `/transfer/agents/${channel}`,
  teams: (channel?: Channels) => `/transfer/teams/${channel}`, // Add this new endpoint
  tasks: '/tasks',
  updateTask: (uuid: string) => `/tasks/${uuid}`,
  getOrCreateTaskActivity: (task_uuid: string) => `/tasks/${task_uuid}/activities`,
  updateOrDeleteTaskActivity: (task_uuid: string, activity_uuid: string) =>
    `/tasks/${task_uuid}/activities/${activity_uuid}`,

  LiveConversationInView: () => `/views/active/conversations?channel=whatsapp,chat,call`,
  // call related
  holdCall: (callUUID: string) => `/call/${callUUID}/hold`,
  recording: (callUUID: string) => `/call/${callUUID}/recording`,

  // transfer related (invite participant)
  mergeParticipant: (isExternal: boolean) =>
    `/contacto/transfer/call/warm/merge${isExternal ? '?external=true' : ''}`,
  switchCall: `/contacto/transfer/call/warm/switch`,
  hangupParticipant: `/contacto/transfer/call/warm/hangup`,
  cancelCallTransfer: (type: 'warm' | 'cold') => `/contacto/transfer/call/${type}/stop`,
  initiateCallTransfer: (transferType: 'warm' | 'cold' | 'external', isExternalWarm: boolean) =>
    `/contacto/transfer/call/${transferType}${isExternalWarm ? '?warm=true' : ''}`,

  // Monitor related
  monitor: '/monitor',
  coach: '/coach',
  barge: '/barge',
}

/**
 * Creates an instance of the AuthService.
 * @returns An object with methods for authentication.
 */
const serviceFactory = (): ConversationService => {
  const service = createService('/conversations', undefined, BackendServiceName.workflow, 'v1')
  const coreService = createService('', undefined, BackendServiceName.core, 'v1')
  const contactService = createService('/contacts', undefined, BackendServiceName.workflow, 'v1')
  const workflowService = createService('', undefined, BackendServiceName.workflow, 'v1')

  return {
    getChunks: (conversationNumber, start, end) =>
      service.get(CONVERSATION_ENDPOINTS.getChunks(conversationNumber, start, end), '', {
        validateStatus: (status) => status === 200 || status === 404,
      }),
    getActiveConversations: () =>
      service.get(CONVERSATION_ENDPOINTS.activeConversations, '', {
        validateStatus: (status) => status === 200 || status === 404,
      }),
    updateConversation: (payload, conversationNumber) =>
      service.patch(CONVERSATION_ENDPOINTS.updateConversation(conversationNumber), payload),
    addChunk: (payload: Partial<ChunkPayload>, conversationNumber) =>
      service.post(CONVERSATION_ENDPOINTS.addChunk(conversationNumber), payload),
    createView: (Payload: Partial<ViewCreatePayload>) =>
      coreService.post(CONVERSATION_ENDPOINTS.views(), Payload),
    updateView: ({ uuid, payload }: { uuid: string; payload: Partial<ViewCreatePayload> }) =>
      coreService.put(CONVERSATION_ENDPOINTS.updateView(uuid), payload),

    exportView: ({ uuid, payload }: { uuid: string; payload: Partial<ExportViewPayload> }) =>
      coreService.post(CONVERSATION_ENDPOINTS.exportView(uuid), payload),
    exportRecording: (viewUuid, conversationId, recordingId) =>
      coreService.get(
        CONVERSATION_ENDPOINTS.exportRecording(viewUuid, conversationId, recordingId),
      ),
    getTranscription: (recordingUUID) =>
      workflowService.get(CONVERSATION_ENDPOINTS.getTranscription(recordingUUID)),
    //  Views
    getViews: () => coreService.get(CONVERSATION_ENDPOINTS.views()),
    getViewDetails: (uuid: string) => coreService.get(CONVERSATION_ENDPOINTS.viewDetails(uuid)),
    getRecordingDetails: (conv_number: string) =>
      coreService.get(CONVERSATION_ENDPOINTS.getRecordingDetails(conv_number)),
    getViewRecords: (params: ViewRecordsParams) =>
      coreService.post(CONVERSATION_ENDPOINTS.viewRecords, params),
    getConversationDetail: (conversationNumber: string, queryParams?: Record<string, unknown>) =>
      coreService.get(CONVERSATION_ENDPOINTS.conversationDetail(conversationNumber, queryParams)),
    getConversationFilterConfig: () =>
      coreService.get(CONVERSATION_ENDPOINTS.conversationFiltersConfiguration),
    getRecentContacts: (channel: Channels, senderId?: string) =>
      contactService.get(CONVERSATION_ENDPOINTS.recentContacts(channel, senderId)),
    getLiveAgents: (channel?: Channels) =>
      coreService.get(CONVERSATION_ENDPOINTS.liveAgents(channel)),
    getLiveTeams: (channel?: Channels) => coreService.get(CONVERSATION_ENDPOINTS.teams(channel)), // Add this new method
    // Task related
    getTasks: (payload: Partial<TaskSearchPayload>) =>
      workflowService.post(`${CONVERSATION_ENDPOINTS.tasks}/list`, payload),
    createTask: (payload: TaskPayload) =>
      workflowService.post(CONVERSATION_ENDPOINTS.tasks, payload),
    updateTask: (payload: Partial<TaskPayload>, taskUuid: string) =>
      workflowService.patch(CONVERSATION_ENDPOINTS.updateTask(taskUuid), payload),
    getTaskActivities: (taskUuid: string) =>
      workflowService.get(CONVERSATION_ENDPOINTS.getOrCreateTaskActivity(taskUuid)),
    createTaskActivity: (payload: TaskActivityPayload, taskUuid: string) =>
      workflowService.post(CONVERSATION_ENDPOINTS.getOrCreateTaskActivity(taskUuid), payload),
    updateTaskActivity: (payload: TaskActivityPayload, taskUuid: string, activityUuid: string) =>
      workflowService.patch(
        CONVERSATION_ENDPOINTS.updateOrDeleteTaskActivity(taskUuid, activityUuid),
        payload,
      ),
    deleteTaskActivity: (params: DeleteTaskActivityPayload | undefined) =>
      workflowService.delete(
        CONVERSATION_ENDPOINTS.updateOrDeleteTaskActivity(
          params?.taskUuid || '',
          params?.activityUuid || '',
        ),
      ),
    getLiveConversationInViews: (payload) =>
      coreService.post(CONVERSATION_ENDPOINTS.LiveConversationInView(), payload),
    favoriteView: ({
      uuid,
      payload,
    }: {
      uuid: string
      payload: {
        favourite: boolean
      }
    }) => coreService.patch(CONVERSATION_ENDPOINTS.favouriteView(uuid), payload),
    // call related
    holdCall: (payload: HoldCallPayload, callUUID: string) =>
      coreService.post(CONVERSATION_ENDPOINTS.holdCall(callUUID), payload),
    toggleRecording: (payload: RecordingPayload, callUUID: string) =>
      coreService.post(CONVERSATION_ENDPOINTS.recording(callUUID), payload),
    toggleCall: (payload: Partial<TransferPayload>) =>
      coreService.post(CONVERSATION_ENDPOINTS.switchCall, payload),
    mergeParticipant: (payload, isExternal) =>
      coreService.post(CONVERSATION_ENDPOINTS.mergeParticipant(isExternal), payload),
    hangupParticipant: (payload: Partial<TransferPayload>) =>
      coreService.post(CONVERSATION_ENDPOINTS.hangupParticipant, payload),
    cancelCallTransfer: (payload: Partial<TransferPayload>, type: 'warm' | 'cold') =>
      coreService.post(CONVERSATION_ENDPOINTS.cancelCallTransfer(type), payload),
    initiateCallTransfer: (payload, transferType) =>
      coreService.post(
        CONVERSATION_ENDPOINTS.initiateCallTransfer(
          payload.transfer_type === 'external number' ? 'external' : transferType,
          payload.transfer_type === 'external number' && transferType === 'warm',
        ),
        payload,
      ),

    // Monitor related methods
    startMonitoring: (payload) => coreService.post(CONVERSATION_ENDPOINTS.monitor, payload),
    startCoaching: (payload) => coreService.post(CONVERSATION_ENDPOINTS.coach, payload),
    startBarging: (payload) => coreService.post(CONVERSATION_ENDPOINTS.barge, payload),

    downloadRecordings: (payload) =>
      coreService.post(CONVERSATION_ENDPOINTS.downloadRecordings, payload),
  }
}

/**
 * A service instance for the authentication API or the Hodor Service (Global)
 * @returns Available methods for authentication.
 */
export const conversationService = serviceFactory()
